import { getFirmwareApiGateway } from 'common';

import {
  isFirmwareOverrideEnabled,
  getFirmwareOverrides,
  createMockAsyncState,
  shouldOverrideEndpoint,
  type FirmwareOverrides,
} from '../../utils/firmwareConfig';
import { HttpClient } from '../../utils/httpClient';
import { useAsyncState } from '../../utils/useAsyncState';

export interface IotStatus {
  connected: boolean;
  hub_id: string;
  last_connection: string;
  provisioned: boolean;
}

export interface BaseStationConfig {
  device_name: string;
  lock_enabled: boolean;
  lock_pin: string | null;
  setup_mode: boolean;
  serial_number: string;
}

export interface HardwareVersion {
  version: string;
}

export interface SoftwareVersion {
  version: string;
}

export interface Timestamp {
  current_time: string;
}

interface UseConfigurationOptions {
  isOverride?: boolean;
  overrideIotStatus?: IotStatus;
  customOverrides?: FirmwareOverrides;
}

export function useConfiguration(options?: UseConfigurationOptions) {
  const httpClient = new HttpClient(getFirmwareApiGateway());

  // Check if build-time override is enabled or manual override is requested
  const isOverrideMode = options?.isOverride || isFirmwareOverrideEnabled();
  const overrides = options?.customOverrides || getFirmwareOverrides();

  // IoT Status - use override, custom override, or API call
  const iotStatus = isOverrideMode
    ? createMockAsyncState(options?.overrideIotStatus || overrides.iotStatus!)
    : useAsyncState(() => httpClient.get<IotStatus>('/iot/status'));

  // Base Station Config - use override or API call
  const baseStationConfig =
    isOverrideMode && overrides.baseStationConfig
      ? createMockAsyncState(overrides.baseStationConfig)
      : useAsyncState(() => httpClient.get<BaseStationConfig>('/config/base-station'));

  // Hardware Version - use override or API call
  const hardwareVersion =
    isOverrideMode && overrides.hardwareVersion
      ? createMockAsyncState(overrides.hardwareVersion)
      : useAsyncState(() => httpClient.get<HardwareVersion>('/hw_version'));

  // Software Version - use override or API call
  const softwareVersion =
    isOverrideMode && overrides.softwareVersion
      ? createMockAsyncState(overrides.softwareVersion)
      : useAsyncState(() => httpClient.get<SoftwareVersion>('/sw_version'));

  // Timestamp - always use current time for override or API call
  const timestamp =
    isOverrideMode && overrides.timestamp
      ? createMockAsyncState({ current_time: new Date().toISOString() })
      : useAsyncState(() => httpClient.get<Timestamp>('/timestamp'));

  const refreshAll = async () => {
    const promises: Promise<void>[] = [];

    // Only execute API calls for endpoints that are not overridden
    if (!isOverrideMode || !shouldOverrideEndpoint('/iot/status')) {
      promises.push(iotStatus.execute());
    }
    if (!isOverrideMode || !shouldOverrideEndpoint('/config/base-station')) {
      promises.push(baseStationConfig.execute());
    }
    if (!isOverrideMode || !shouldOverrideEndpoint('/hw_version')) {
      promises.push(hardwareVersion.execute());
    }
    if (!isOverrideMode || !shouldOverrideEndpoint('/sw_version')) {
      promises.push(softwareVersion.execute());
    }
    if (!isOverrideMode || !shouldOverrideEndpoint('/timestamp')) {
      promises.push(timestamp.execute());
    }

    await Promise.all(promises);
  };

  const setLockEnabled = async (enabled: boolean) => {
    if (isOverrideMode && shouldOverrideEndpoint('/config/base-station')) {
      // In override mode, simulate the API call without actually making it
      console.log(`🔧 [Override] Would set lock enabled to: ${enabled}`);
      if (baseStationConfig.data) {
        baseStationConfig.setData({ ...baseStationConfig.data, lock_enabled: enabled });
      }
      return;
    }

    try {
      await httpClient.post('/config/base-station', { lock_enabled: enabled });
      await baseStationConfig.execute(); // Refresh data
    } catch (error) {
      throw new Error(`Failed to set lock enabled: ${error}`);
    }
  };

  const setLockPin = async (pin: string | null) => {
    if (isOverrideMode && shouldOverrideEndpoint('/config/base-station')) {
      // In override mode, simulate the API call without actually making it
      console.log(`🔧 [Override] Would set lock pin to: ${pin ? '[HIDDEN]' : 'null'}`);
      if (baseStationConfig.data) {
        baseStationConfig.setData({ ...baseStationConfig.data, lock_pin: pin });
      }
      return;
    }

    try {
      await httpClient.post('/config/base-station', { lock_pin: pin });
      await baseStationConfig.execute(); // Refresh data
    } catch (error) {
      throw new Error(`Failed to set lock pin: ${error}`);
    }
  };

  const updateBaseStationConfig = async (config: Partial<BaseStationConfig>) => {
    if (isOverrideMode && shouldOverrideEndpoint('/config/base-station')) {
      // In override mode, simulate the API call without actually making it
      console.log('🔧 [Override] Would update base station config:', config);
      if (baseStationConfig.data) {
        baseStationConfig.setData({ ...baseStationConfig.data, ...config });
      }
      return;
    }

    try {
      await httpClient.post('/config/base-station', config);
      await baseStationConfig.execute(); // Refresh data
    } catch (error) {
      throw new Error(`Failed to update base station config: ${error}`);
    }
  };

  return {
    iotStatus,
    baseStationConfig,
    hardwareVersion,
    softwareVersion,
    timestamp,
    refreshAll,
    setLockEnabled,
    setLockPin,
    updateBaseStationConfig,
  };
}
